import React, { useState, useRef } from 'react';
import { Block } from '@components/_common/Block/index.css';
import { Formik, Form } from 'formik';
import FormLabel from '@components/_common/_form/FormLabel';
import TextField from '@components/_common/_form/TextField/TextField';
import PhoneField from '@components/_common/_form/PhoneField/PhoneField';
import TextArea from '@components/_common/_form/TextArea';
import Button from '@components/_common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import useResizeObserver from 'use-resize-observer';
import { install } from 'resize-observer';
import { APIContact } from '@api/contactApi';
import { useSelector } from 'react-redux';
import { IStore } from '@interfaces/logic/redux';
import { useMSTranslation } from '@utils/useMSTranslation';

const PictoCont = styled.div`
    font-size: 96px;
`;

const AnimatedCont = styled.div`
    transition: opacity 500ms, transform 500ms;
    &.enter {
        opacity: 0;
    }
    &.enter-active {
        opacity: 1;
    }
    &.enter-done {
        opacity: 1;
    }
    &.exit {
        opacity: 1;
    }
    &.exit-active {
        opacity: 0;
    }
    &.exit-done {
        opacity: 0;
    }
`;

const AnimatedHeight = styled.div`
    transition: height 0.25s ease;
`;

const HomeContactBlock = () => {
    const { __ } = useMSTranslation(['home', 'struct']);

    const ctoken = useSelector((state: IStore) => state.Auth.ctoken);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    // Hack resize observer Mac OS + Safari
    if (typeof window !== 'undefined') {
        install();
    }

    const ref = useRef<HTMLDivElement>(null);
    const { height } = useResizeObserver({ ref });

    interface InfosContact {
        name: string;
        email: string;
        phone: string;
        description: string;
    }

    const initialValues: InfosContact = {
        name: '',
        email: '',
        phone: '',
        description: ''
    };

    const onSubmit = (values: InfosContact) => {
        if (ctoken) {
            setLoading(true);
            APIContact.requestDemo(ctoken, values)
                .then(() => {
                    setSubmitted(true);
                    setLoading(false);
                })
                .catch(err => {
                    console.log(err);
                });
        }
    };

    return (
        <Block className='md:p-4 p-2 mb-2'>
            <AnimatedHeight style={{ height: height }}>
                <div ref={ref}>
                    <SwitchTransition>
                        <CSSTransition
                            key={submitted ? '1' : '2'}
                            timeout={500}
                        >
                            {!submitted ? (
                                <AnimatedCont>
                                    <h2 className='text-xl text-main text-center mb-2'>
                                        {__('Contactez-nous', 'home')}
                                    </h2>
                                    <p className='text-center opacity-50 leading-normal'>
                                        {__(
                                            'Vous souhaitez une démo gratuite du logiciel Viseet.me ? Remplissez tous les champs et nous vous contacterons rapidement.',
                                            'home'
                                        )}
                                    </p>
                                    <p className='text-center opacity-50 leading-normal'>
                                        {__(
                                            'Vous pouvez également réserver directement un créneau avec Maxence en',
                                            'home'
                                        )}{' '}
                                        <a
                                            href='https://calendly.com/viseet'
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            {__(
                                                'cliquant sur ce lien',
                                                'struct'
                                            )}
                                        </a>
                                        .
                                    </p>
                                    <Formik
                                        initialValues={initialValues}
                                        onSubmit={onSubmit}
                                    >
                                        {() => (
                                            <Form>
                                                <div>
                                                    <FormLabel htmlFor='name'>
                                                        {__('Nom', 'struct')}
                                                    </FormLabel>
                                                    <TextField
                                                        name='name'
                                                        placeholder='ex: John Doe'
                                                    />
                                                </div>
                                                <div className='mt-2'>
                                                    <FormLabel htmlFor='email'>
                                                        {__(
                                                            'Adresse e-mail',
                                                            'struct'
                                                        )}
                                                    </FormLabel>
                                                    <TextField
                                                        name='email'
                                                        placeholder={__(
                                                            'Votre adresse e-mail',
                                                            'struct'
                                                        )}
                                                    />
                                                </div>
                                                <div className='mt-2'>
                                                    <FormLabel htmlFor='phone'>
                                                        {__(
                                                            'Téléphone',
                                                            'struct'
                                                        )}
                                                    </FormLabel>
                                                    <PhoneField name='phone' />
                                                </div>
                                                <div className='mt-2'>
                                                    <FormLabel htmlFor='description'>
                                                        {__(
                                                            'Description du projet',
                                                            'home'
                                                        )}
                                                    </FormLabel>
                                                    <TextArea name='description' />
                                                </div>
                                                <div className='mt-4 text-center'>
                                                    <Button
                                                        variant='action'
                                                        type='submit'
                                                        isLoading={loading}
                                                        loadingText={
                                                            __(
                                                                'Envoi de la demande en cours',
                                                                'home'
                                                            ) + '...'
                                                        }
                                                    >
                                                        {__(
                                                            'Envoyer la demande',
                                                            'home'
                                                        )}
                                                    </Button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </AnimatedCont>
                            ) : (
                                <AnimatedCont>
                                    <PictoCont className='text-action mb-4'>
                                        <FontAwesomeIcon
                                            className='block m-auto'
                                            icon={['far', 'paper-plane']}
                                        />
                                    </PictoCont>
                                    <h2 className='text-xl text-main text-center mb-2'>
                                        {__(
                                            'Votre demande a été prise en compte',
                                            'home'
                                        )}
                                    </h2>
                                    <p className='text-center opacity-50 leading-normal'>
                                        {__(
                                            "Merci pour votre demande. L'équipe Viseet.me vous contactera dans les plus brefs délais.",
                                            'home'
                                        )}
                                    </p>
                                    <div className='mt-2 text-center'>
                                        <Button
                                            variant='clear'
                                            backIco
                                            onClick={() => setSubmitted(false)}
                                        >
                                            {__('Retour', 'struct')}
                                        </Button>
                                    </div>
                                </AnimatedCont>
                            )}
                        </CSSTransition>
                    </SwitchTransition>
                </div>
            </AnimatedHeight>
        </Block>
    );
};

export default HomeContactBlock;
